import type { QueryClient } from "@tanstack/react-query";

import { flush } from "./flush";
import { getSnapshot, refresh } from "./store";

export type Connection = "unknown" | "online" | "offline";
export type SyncStatus = {
  connection: Connection;
  syncing: boolean;
  /** The session expired; changes wait until the user signs in again. */
  needsSignIn: boolean;
  lastSyncedAt: number | null;
  lastError: string | null;
  nextRetryAt: number | null;
};

let status: SyncStatus = {
  connection: "unknown",
  syncing: false,
  needsSignIn: false,
  lastSyncedAt: null,
  lastError: null,
  nextRetryAt: null,
};
const listeners = new Set<() => void>();

export function subscribeStatus(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}
export function getStatus(): SyncStatus {
  return status;
}
function setStatus(patch: Partial<SyncStatus>) {
  status = { ...status, ...patch };
  listeners.forEach((l) => l());
}

let userId: number | null = null;
let queryClient: QueryClient | null = null;
let running = false;
let again = false;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let failures = 0;
let started = false;

const MAX_BACKOFF_MS = 60_000;
const hasPending = () => userId !== null && getSnapshot().pending.some((o) => o.userId === userId);

/** Any request's outcome tells us about the connection. Coming back online triggers a sync. */
export function reportConnection(ok: boolean): void {
  const was = status.connection;
  setStatus({ connection: ok ? "online" : "offline" });
  if (ok && was === "offline") {
    failures = 0;
    void syncNow();
  }
}

export function startSync(uid: number, qc: QueryClient): void {
  userId = uid;
  queryClient = qc;
  setStatus({ needsSignIn: false });
  if (!started) {
    started = true;
    window.addEventListener("online", () => {
      failures = 0;
      void syncNow();
    });
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") void syncNow();
    });
  }
  void refresh().then(syncNow);
}

export function stopSync(): void {
  userId = null;
  clearTimeout(retryTimer);
}

/** Send pending changes now (or right after the current attempt finishes). */
export async function syncNow(): Promise<void> {
  if (userId === null) return;
  if (running) {
    again = true;
    return;
  }
  running = true;
  clearTimeout(retryTimer);
  setStatus({ syncing: true, nextRetryAt: null });
  try {
    do {
      again = false;
      const uid = userId;
      if (uid === null) break;
      const result = await flush(uid);
      if (result.sent.length > 0) {
        setStatus({ lastSyncedAt: Date.now(), connection: "online", lastError: null });
        // Fetch the server's version, which now includes these changes.
        await Promise.all([
          queryClient?.invalidateQueries({ queryKey: ["summary"] }),
          result.sent.some((o) => o.kind === "createFoodAndLog") &&
            queryClient?.invalidateQueries({ queryKey: ["foods"] }),
        ]);
      }
      if (result.stopped === "auth") {
        setStatus({ needsSignIn: true, lastError: result.error ?? null });
      } else if (result.stopped === "retry") {
        // Back off only while nothing gets through; any progress means the connection works.
        if (result.sent.length > 0) failures = 0;
        setStatus({ lastError: result.error ?? null, connection: "offline" });
        scheduleRetry();
      } else {
        failures = 0;
      }
    } while (again);
  } finally {
    running = false;
    setStatus({ syncing: false });
  }
}

function scheduleRetry() {
  const delay = Math.min(MAX_BACKOFF_MS, 2_000 * 2 ** failures);
  failures++;
  setStatus({ nextRetryAt: Date.now() + delay });
  retryTimer = setTimeout(() => {
    if (hasPending()) void syncNow();
  }, delay);
}
